/**
 * Tenant-side System Catalog hooks — Studio R1 fix (PR #459 audit).
 *
 * The agent builder's tenant adapter previously called sysadmin-only
 * hooks (`useWrapperFunctions` → `/admin/platform/argus-wrapper-functions`
 * and `useCatalogWorkflows` → `/admin/platform/catalog/workflows`),
 * yielding 8× 401 per session for non-sysadmin users and an empty
 * Functions + Workflows picker even when 7 published system functions
 * existed server-side.
 *
 * This file routes the tenant builder to the redacted public-facing
 * endpoints that DO authorize tenants:
 *
 *   /api/v1/functions/system  → tenantFunctionsRoutes (PR-G)
 *   /api/v1/workflows/system  → tenantSystemWorkflowsRoutes (PR-K1)
 *
 * Both endpoints return the I-M6.7 redacted shape — no source code,
 * no signing keys, no internal IDs beyond the row UUID. Exactly the
 * surface the builder needs to render the Functions + Workflows
 * pickers.
 *
 * The sysadmin scope continues to use the authoring hooks in
 * use-argus-models.ts / use-sysadmin-catalog-workflows.ts — those
 * surfaces still need draft / unpublished / source visibility.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Functions (PR-G) ──────────────────────────────────────────────────────

export interface TenantSystemFunction {
  id: string;
  functionKey: string;
  displayName: string;
  description: string;
  category: string;
  appliesTo: 'inlet' | 'outlet' | 'both';
  latencyBudgetMs: number;
  version: string;
  publishedAt: string;
}

export interface TenantSystemFunctionsResponse {
  items: TenantSystemFunction[];
}

/**
 * Lists sysadmin-authored wrapper functions that have been published
 * (status='published') and are runtime-active. Tenants of any role
 * can call this — it's discovery surface for the agent builder.
 */
export function useTenantSystemFunctions() {
  return useQuery<TenantSystemFunctionsResponse, Error>({
    queryKey: ['tenant-system-functions'],
    queryFn: () =>
      apiClient.get<TenantSystemFunctionsResponse>('/functions/system'),
    staleTime: 60_000,
  });
}

// ─── Workflows (PR-K1) ─────────────────────────────────────────────────────

export interface TenantSystemWorkflow {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  category: string;
  definition: Record<string, unknown>;
  triggerConfig: Record<string, unknown>;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  referencedSkillSlugs: string[];
  referencedToolSlugs: string[];
  referencedFunctionKeys: string[];
  sourceVersion: number;
}

export interface TenantSystemWorkflowsResponse {
  items: TenantSystemWorkflow[];
}

/**
 * Lists sysadmin-published system workflow templates available to
 * the calling tenant to clone. Tenants of any role can read this —
 * it's the picker source for the agent builder + the clone-source
 * for the tenant_workflows surface.
 */
export function useTenantSystemWorkflows() {
  return useQuery<TenantSystemWorkflowsResponse, Error>({
    queryKey: ['tenant-system-workflows'],
    queryFn: () =>
      apiClient.get<TenantSystemWorkflowsResponse>('/workflows/system'),
    staleTime: 60_000,
  });
}
